/**
 * Lehká reference na destinaci (sdílený contract s backend `DestinationRef`).
 * Je součástí read modelu stromu (`PlanningProjectNode.destinations`).
 */
export interface DestinationRef {
  destinationId: number;
  name: string;
  code?: string | null;
}

/**
 * Lehká reference na osobu (Party). `partyId` = Party.Id aktuálně platné osoby.
 * UI hodnota odpovědných osob ve formulářích projektu i série.
 */
export interface PersonRef {
  partyId: number;
  fullName: string;
}

export interface PlanningTermNode {
  termId: string;
  legacyTermId: number;
  name: string;
  dateFrom?: string | null;
  nights?: number | null;
  aggregatedStatus: string;
}

export interface PlanningSerieNode {
  productSerieId: string;
  name: string;
  mainProgramLengthDays: number;
  isArchived: boolean;
  terms: PlanningTermNode[];
}

export interface PlanningProductNode {
  productId: string;
  legacyProductId: number;
  name: string;
  productType: string;
  series: PlanningSerieNode[];
}

export interface PlanningProjectNode {
  projectId: string;
  name: string;
  isArchived: boolean;
  destinations: DestinationRef[];
  products: PlanningProductNode[];
}

export interface GetPlanningTreeResponse {
  projects: PlanningProjectNode[];
}

interface ProblemDetailsLike {
  title?: string;
  detail?: string;
  status?: number;
}

export class PlannerApiError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = 'PlannerApiError';
  }
}

/**
 * React-query klíče pro planner slice. Zatím jen agregovaný read model stromu —
 * zakládací/search/configuration vrstvy vzniknou znovu s novými návrhy.
 */
export const plannerQueryKeys = {
  all: ['planner'] as const,
  tree: () => [...plannerQueryKeys.all, 'tree'] as const,
} as const;

async function plannerRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    let message = `Request failed with status ${response.status}.`;

    try {
      const body = (await response.json()) as ProblemDetailsLike;
      message = body.detail ?? body.title ?? message;
    } catch {
      // Ignore invalid body and use generic message.
    }

    throw new PlannerApiError(message, response.status);
  }

  return (await response.json()) as T;
}

export function getPlanningTree(): Promise<GetPlanningTreeResponse> {
  return plannerRequest<GetPlanningTreeResponse>('/api/venua-planner/tree');
}
